<template>
  <div class="pc-ticket">
    <!-- hero 区渐变：独立 fixed 层；nav 纹理仍在 __hero -->
    <div class="pc-ticket__hero-gradient-bg" aria-hidden="true"></div>
    <div
      ref="ticketTopStack"
      class="pc-ticket__top-stack"
      :style="ticketTopStackStyle"
    >
      <!-- 顶部header -->
      <header class="pc-ticket__hero">
        <img
          class="pc-ticket__kv"
          src="@/assets/image/ticket/header_bg.webp"
          alt="Ticket page header background illustration (decorative)"
          width="1798"
          height="1135"
          decoding="async"
          loading="lazy"
        />
        <img
          class="pc-ticket__title-img"
          src="@/assets/image/ticket/title.webp"
          alt="Ticket application page title graphic"
          width="769"
          height="66"
          decoding="async"
          loading="lazy"
        />
        <img
          class="pc-ticket__deco pc-ticket__deco--left"
          src="@/assets/image/ticket/nav_icon_circular.webp"
          alt="Decorative circular graphic (left)"
          width="381"
          height="381"
          decoding="async"
          loading="lazy"
        />
        <img
          class="pc-ticket__deco pc-ticket__deco--right"
          src="@/assets/image/ticket/nav_icon_star.webp"
          alt="Decorative star graphic (right)"
          width="319"
          height="319"
          decoding="async"
          loading="lazy"
        />

        <div class="pc-ticket__marquee">
          <div class="pc-ticket__marquee-view">
            <div class="pc-ticket__marquee-track">
              <div
                v-for="dup in 2"
                :key="'m-' + dup"
                class="pc-ticket__marquee-chunk"
                :aria-hidden="dup === 2 ? 'true' : undefined"
              >
                <img
                  v-for="item in 10"
                  :key="`${dup}-${item}`"
                  class="pc-ticket__marquee-img"
                  src="@/assets/image/index/banner_footer_title.webp"
                  width="314"
                  height="30"
                  alt="Scrolling footer title strip (decorative)"
                  aria-hidden="true"
                  decoding="async"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>

        <section class="pc-ticket__panel" aria-label="Ticketing and streaming">
          <div class="pc-ticket__lead">
            <div class="pc-ticket__brand">
              <img
                class="pc-ticket__brand-logo"
                src="@/assets/image/logo/yostation.webp"
                width="100"
                height="100"
                alt="YoStation logo"
                decoding="async"
                loading="lazy"
              />
              <span class="pc-ticket__brand-name">{{
                pcTicketCopy.yoStation.brandName
              }}</span>
            </div>
            <div class="pc-ticket__station">
              <p class="pc-ticket__tags">
                <span>{{ pcTicketCopy.yoStation.tagPrimary }}</span>
                <span>{{ pcTicketCopy.yoStation.tagSecondary }}</span>
              </p>
              <ul class="pc-ticket__cells">
                <li
                  v-for="(cell, i) in pcTicketCopy.yoStation.cellLabels"
                  :key="i"
                >
                  {{ cell }}
                </li>
              </ul>
              <nav class="pc-ticket__actions" aria-label="YoStation links">
                <button
                  type="button"
                  class="pc-ticket__action pc-ticket__action--gold"
                  @click="openTicketQrModal"
                >
                  {{ pcTicketCopy.yoStation.downloadCta }}
                  <img
                    class="pc-ticket__action-ico"
                    src="@/assets/image/index/icon_open_other_1.webp"
                    width="14"
                    height="14"
                    alt=""
                    aria-hidden="true"
                    decoding="async"
                    loading="lazy"
                  />
                </button>
                <a
                  :href="pcTicketLinks.ticketInquiryForm"
                  class="pc-ticket__action pc-ticket__action--cyan"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ pcTicketCopy.yoStation.inquiryCta }}
                  <img
                    class="pc-ticket__action-ico"
                    src="@/assets/image/index/icon_open_other_2.webp"
                    width="14"
                    height="14"
                    alt="External link icon — ticket inquiry contact form"
                    decoding="async"
                    loading="lazy"
                  />
                </a>
              </nav>
            </div>
          </div>

          <img
            class="pc-ticket__line"
            src="@/assets/image/ticket/card_line.webp"
            alt=""
            width="1209"
            height="70"
            decoding="async"
            loading="lazy"
            role="presentation"
          />

          <article
            v-for="tier in ticketTiersWithRows"
            :key="tier.id"
            class="pc-ticket__tier"
            :class="'pc-ticket__tier--' + tier.variant"
          >
            <header class="pc-ticket__tier-hd">
              <span class="pc-ticket__tier-no" aria-hidden="true">{{
                tier.id
              }}</span>
              <div class="pc-ticket__tier-body">
                <div class="pc-ticket__tier-meta">
                  <h2 class="pc-ticket__tier-name">
                    <span>{{ tier.num }}</span>
                    <span>{{ tier.title }}</span>
                  </h2>
                  <p class="pc-ticket__tier-price">{{ tier.priceLabel }}</p>
                </div>
              </div>
            </header>
            <dl class="pc-ticket__tier-dl">
              <div
                v-for="row in tier.rows"
                :key="tier.id + '-' + row.label"
                class="pc-ticket__tier-pair"
              >
                <dt class="pc-ticket__tier-term">{{ row.label }}</dt>
                <dd class="pc-ticket__tier-desc">{{ row.value }}</dd>
              </div>
            </dl>
          </article>

          <img
            class="pc-ticket__line pc-ticket__line--gap"
            src="@/assets/image/ticket/card_line.webp"
            alt=""
            width="1209"
            height="70"
            decoding="async"
            loading="lazy"
            role="presentation"
          />

          <div
            class="pc-ticket__notes"
            role="region"
            aria-labelledby="ticket-notes-h"
          >
            <h2
              id="ticket-notes-h"
              class="pc-ticket__notes-h"
              v-html="pcTicketCopy.noticeHeadingHtml"
            ></h2>
            <ul class="pc-ticket__notes-ul">
              <li v-for="(line, idx) in noticeLines" :key="'n-' + idx">
                <span v-html="'※' + line"></span>
              </li>
            </ul>
          </div>
        </section>
      </header>
    </div>

    <!-- 底部跑马灯保留在文档流里，跟随下方内容一起滚动 -->
    <div
      ref="marqueeBottomSlot"
      class="pc-ticket__marquee-bottom-slot"
      aria-hidden="true"
    >
      <div class="pc-ticket__marquee pc-ticket__marquee--bottom">
        <div class="pc-ticket__marquee-view">
          <div class="pc-ticket__marquee-track">
            <div
              v-for="dup in 2"
              :key="'mb-' + dup"
              class="pc-ticket__marquee-chunk"
            >
              <img
                v-for="item in 10"
                :key="`mb-${dup}-${item}`"
                class="pc-ticket__marquee-img"
                src="@/assets/image/index/banner_footer_title.webp"
                width="314"
                height="30"
                alt="Scrolling footer title strip (decorative)"
                aria-hidden="true"
                decoding="async"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 与首页 pc-index__dj-wrap-bg-layer 同款：背景无限横向滚动；主区内容可自行补充 -->
    <section class="pc-ticket__main" aria-label="Ticket page content">
      <div class="pc-ticket__main-bg-layer" aria-hidden="true"></div>

      <div class="pc-ticket__main-content">
        <div
          ref="ticketTimeline"
          class="pc-ticket__main-content-time-line"
          :class="{ 'is-cards-revealed': timelineCardsRevealed }"
        >
          <div class="pc-ticket__main-content-time-line-item-year">
            {{ ticketTimelineYear }}
          </div>
          <div
            v-for="city in ticketTimelineCities"
            :key="city.id"
            class="pc-ticket__main-content-time-line-item"
          >
            <div class="pc-ticket__main-content-time-line-item-time">
              {{ city.dateLine
              }}<span
                :class="
                  city.dowDistinguished
                    ? 'pc-ticket__main-content-time-line-item-time-dow'
                    : null
                "
                >{{ city.dow }}</span
              >
            </div>
            <div
              class="pc-ticket__main-content-time-line-item-content"
              :class="'pc-ticket__main-content-time-line-item--' + city.variant"
            >
              <div
                v-for="(row, ri) in city.scheduleLines"
                :key="city.id + '-' + ri"
                class="pc-ticket__main-content-time-line-item-content-line"
              >
                <span class="label">{{ row.label }}</span>
                <span class="value">{{ row.value }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <transition name="pc-ticket-qr-modal">
      <div
        v-show="ticketQrModalVisible"
        class="pc-ticket-qr-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pc-ticket-qr-modal-title"
        @click.self="closeTicketQrModal"
      >
        <h2 id="pc-ticket-qr-modal-title" class="pc-ticket-qr-modal__sr-only">
          {{ pcTicketCopy.ticketQrModal.dialogTitle }}
        </h2>
        <button
          type="button"
          class="pc-ticket-qr-modal__close"
          :aria-label="pcTicketCopy.ticketQrModal.closeLabel"
          @click="closeTicketQrModal"
        >
          <img
            src="@/assets/image/detail/close.webp"
            width="40"
            height="40"
            alt=""
            decoding="async"
          />
        </button>
        <div class="pc-ticket-qr-modal__inner">
          <div class="pc-ticket-qr-modal__qr-frame">
            <img
              class="pc-ticket-qr-modal__qr"
              src="@/assets/image/logo/qr.webp"
              width="360"
              height="360"
              :alt="pcTicketCopy.imgAlt.yoStationQr"
              decoding="async"
            />
          </div>
          <p class="pc-ticket-qr-modal__caption">
            {{ pcTicketCopy.ticketQrModal.caption }}
          </p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ticketContent from "@/views/data/ticket-content.json";

/**
 * 票务页：文案、列表与外链维护 ticket-content.json；图片路径在模板中写死
 */
export default {
  name: "PcTicketPage",
  data() {
    const page = ticketContent;
    return {
      // ========== 运营 / 页面配置（优先维护）==========
      /** 外链（お問い合わせフォーム等）→ ticket-content.json → pcTicketLinks */
      pcTicketLinks: page.pcTicketLinks,
      pcTicketCopy: page.pcTicketCopy,
      ticketTiers: page.ticketTiers,
      ticketTierDetailRows: page.ticketTierDetailRows,
      noticeLines: page.noticeLines,
      ticketTimelineYear: page.ticketTimelineYear,
      ticketTimelineCities: page.ticketTimelineCities,

      // ========== 运行时状态（一般无需改）==========
      /** YoStation 二维码全屏弹窗 */
      ticketQrModalVisible: false,
      /** 滚动进入时间轴区域后触发卡片展开 */
      timelineCardsRevealed: false,
      /** sticky 顶区的 top 偏移；负值表示“直到底部完全进视口再停住” */
      ticketTopStackStickyTop: "0px",
    };
  },
  computed: {
    ticketTiersWithRows() {
      return this.ticketTiers.map((t) => ({
        ...t,
        rows: this.ticketTierDetailRows,
      }));
    },
    ticketTopStackStyle() {
      return {
        top: this.ticketTopStackStickyTop,
      };
    },
  },
  mounted() {
    this.initTimelineCardsReveal();
    this.initTicketTopSticky();
  },
  beforeDestroy() {
    if (this._ticketTopStackResizeObserver) {
      this._ticketTopStackResizeObserver.disconnect();
      this._ticketTopStackResizeObserver = null;
    }
    if (this._onTicketTopStickyWindowResize) {
      window.removeEventListener("resize", this._onTicketTopStickyWindowResize);
      this._onTicketTopStickyWindowResize = null;
    }
    if (this._onTicketTopStickyWindowLoad) {
      window.removeEventListener("load", this._onTicketTopStickyWindowLoad);
      this._onTicketTopStickyWindowLoad = null;
    }
    document.removeEventListener("keydown", this._onTicketQrModalKeydown);
    if (this.ticketQrModalVisible) {
      document.body.style.overflow = "";
    }
    if (this._timelineIo) {
      this._timelineIo.disconnect();
      this._timelineIo = null;
    }
  },
  methods: {
    initTicketTopSticky() {
      if (typeof window === "undefined") return;
      this.$nextTick(() => {
        this.syncTicketTopStickyOffset();
        this._onTicketTopStickyWindowResize = () =>
          this.syncTicketTopStickyOffset();
        this._onTicketTopStickyWindowLoad = () =>
          this.syncTicketTopStickyOffset();
        window.addEventListener("resize", this._onTicketTopStickyWindowResize, {
          passive: true,
        });
        window.addEventListener("load", this._onTicketTopStickyWindowLoad, {
          passive: true,
        });
        if (
          typeof ResizeObserver !== "undefined" &&
          this.$refs.ticketTopStack
        ) {
          this._ticketTopStackResizeObserver = new ResizeObserver(() => {
            this.syncTicketTopStickyOffset();
          });
          this._ticketTopStackResizeObserver.observe(this.$refs.ticketTopStack);
          if (this.$refs.marqueeBottomSlot) {
            this._ticketTopStackResizeObserver.observe(
              this.$refs.marqueeBottomSlot
            );
          }
        }
      });
    },
    syncTicketTopStickyOffset() {
      if (typeof window === "undefined") return;
      const stack = this.$refs.ticketTopStack;
      const slot = this.$refs.marqueeBottomSlot;
      if (!stack) return;
      const stackHeight = stack.offsetHeight || 0;
      const viewportHeight = window.innerHeight || 0;
      let slotExtraHeight = 0;
      if (slot) {
        const slotStyle = window.getComputedStyle(slot);
        const slotMarginTop = parseFloat(slotStyle.marginTop) || 0;
        slotExtraHeight = Math.max(0, (slot.offsetHeight || 0) + slotMarginTop);
      }
      const stickyTop = Math.min(
        0,
        viewportHeight - stackHeight - slotExtraHeight
      );
      this.ticketTopStackStickyTop = `${stickyTop}px`;
    },
    openTicketQrModal() {
      document.removeEventListener("keydown", this._onTicketQrModalKeydown);
      document.addEventListener("keydown", this._onTicketQrModalKeydown);
      this.ticketQrModalVisible = true;
      document.body.style.overflow = "hidden";
    },
    closeTicketQrModal() {
      this.ticketQrModalVisible = false;
      document.body.style.overflow = "";
      document.removeEventListener("keydown", this._onTicketQrModalKeydown);
    },
    _onTicketQrModalKeydown(e) {
      if (e.key === "Escape") {
        this.closeTicketQrModal();
      }
    },
    initTimelineCardsReveal() {
      if (typeof window === "undefined") return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.timelineCardsRevealed = true;
        return;
      }
      this.$nextTick(() => {
        const el = this.$refs.ticketTimeline;
        if (!el) return;
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.timelineCardsRevealed = true;
                if (this._timelineIo) {
                  this._timelineIo.disconnect();
                  this._timelineIo = null;
                }
              }
            });
          },
          { threshold: 0.14, rootMargin: "0px 0px -6% 0px" }
        );
        this._timelineIo = io;
        io.observe(el);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
/* BEM 类名与模板一致；嵌套按 DOM 父子（__hero → …、__panel → …） */
.pc-ticket {
  box-sizing: border-box;
  text-align: left;

  img {
    box-sizing: border-box;
    vertical-align: middle;
  }

  /* 顶区用 sticky 负责“停住”，主区再以更高层级滑过它 */
  > .pc-ticket__top-stack {
    z-index: 1;
  }

  > .pc-ticket__main {
    position: relative;
    z-index: 3;
  }

  &__top-stack {
    position: sticky;
    z-index: 1;
  }

  &__hero-gradient-bg {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-color: #3c3068;
    background-image: url("@/assets/image/ticket/page_bg.webp");
    background-repeat: no-repeat;
    background-size: 100% 1036px;
    background-position: top center;
  }

  &__hero {
    position: relative;
    width: 100%;
    padding-top: 500px;
    padding-bottom: 134px;
    min-height: 1566px;
    box-sizing: border-box;
    background-image: url("@/assets/image/ticket/nav_bg.webp");
    background-size: 100% auto;
    background-repeat: no-repeat;

    .pc-ticket__kv {
      position: absolute;
      top: 431px;
      left: 69px;
      width: 1798px;
      height: 1135px;
      object-fit: contain;
      object-position: center;
    }

    .pc-ticket__title-img {
      position: absolute;
      top: 181px;
      left: 580px;
      width: 769px;
      object-fit: contain;
      object-position: center;
    }

    .pc-ticket__deco {
      position: absolute;
      object-fit: contain;
      object-position: center;
      transform-origin: center center;
      will-change: transform;
      animation: pc-ticket-deco-beat 1.35s infinite;

      &--left {
        top: 80px;
        left: 264px;
        width: 340px;
      }

      &--right {
        top: 90px;
        right: 208px;
        width: 280px;
      }
    }

    .pc-ticket__panel {
      position: relative;
      z-index: 11;
      margin: 0 auto 60px;
      width: 1200px;
      max-width: calc(100% - 48px);
      padding: 60px 0 56px;
      border: 1px solid #fff380;
      border-radius: 8px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 0;
      background-color: rgba(0, 0, 0, 0.25);

      .pc-ticket__lead {
        margin: 0 auto;
        display: flex;
        align-items: stretch;
        justify-content: center;

        .pc-ticket__brand {
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 160px;
          gap: 12px;

          .pc-ticket__brand-logo {
            display: block;
            width: 100px;
            height: 100px;
            object-fit: contain;
            object-position: center;
          }

          .pc-ticket__brand-name {
            font-size: 18px;
            font-weight: 700;
            color: #fff;
            letter-spacing: 0.02em;
          }
        }

        .pc-ticket__station {
          min-width: 0;
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 20px 0;
          justify-content: center;

          .pc-ticket__tags {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: center;
            gap: 12px 24px;
            margin: 0 20px;
            padding: 6px 20px;
            background: rgba(0, 0, 0, 0.25);
            border-radius: 30px;
            font-size: 14px;
            font-weight: 700;
            color: #36e9ff;
            line-height: 1.4;
          }

          .pc-ticket__cells {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin: 0;
            padding: 0;
            list-style: none;

            li {
              margin: 0 20px;
              padding: 0px 28px;
              text-align: center;
              font-size: 14px;
              font-weight: 600;
              color: #fff;
              line-height: 1.45;
              border: 1px solid rgba(255, 255, 255, 0.85);
              border-radius: 2px;
              box-sizing: border-box;
            }
          }

          .pc-ticket__actions {
            margin-top: 20px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            align-items: flex-end;

            .pc-ticket__action {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              margin: 0 20px;
              padding-bottom: 6px;
              font-size: 14px;
              font-weight: 700;
              text-decoration: none;
              border-bottom-width: 1px;
              border-bottom-style: solid;
              transition: opacity 0.2s ease, filter 0.2s ease;

              &[type="button"] {
                font: inherit;
                color: inherit;
                background: transparent;
                border: none;
                border-bottom-width: 1px;
                border-bottom-style: solid;
                border-bottom-color: currentColor;
                cursor: pointer;
                appearance: none;
                -webkit-appearance: none;
                padding: 0 0 6px;
                margin: 0 20px;
                text-align: inherit;
              }

              &:hover {
                opacity: 0.88;
                filter: brightness(1.08);
              }

              &:active {
                opacity: 0.95;
                filter: brightness(0.92);
              }

              &:focus-visible {
                outline: 2px solid currentColor;
                outline-offset: 3px;
                border-radius: 2px;
              }

              &--gold {
                color: #fff380 !important;
                border-bottom-color: #fff380;
              }

              &--cyan {
                color: #36e9ff;
                border-bottom-color: #36e9ff;
              }

              .pc-ticket__action-ico {
                flex-shrink: 0;
                width: 14px;
                height: 14px;
                object-fit: contain;
                object-position: center;
              }
            }
          }
        }
      }

      .pc-ticket__line {
        display: block;
        width: 100%;
        height: auto;
        object-fit: cover;
        object-position: center;
        margin: 24px 0 32px;

        &--gap {
          margin-top: 36px;
          margin-bottom: 28px;
        }
      }

      .pc-ticket__tier {
        width: 562px;
        margin: 0 auto 24px;
        padding: 0 28px 28px;
        border-radius: 8px;
        box-sizing: border-box;

        &:last-of-type {
          margin-bottom: 0;
        }

        &--gold {
          .pc-ticket__tier-no {
            background: #fff380;
          }

          .pc-ticket__tier-name {
            color: #fff380;
          }
        }

        &--cyan {
          .pc-ticket__tier-no {
            background: #36e9ff;
          }

          .pc-ticket__tier-name {
            color: #36e9ff;
          }
        }

        .pc-ticket__tier-hd {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 4px;
          width: 100%;

          .pc-ticket__tier-no {
            flex-shrink: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            font-size: 16px;
            font-weight: 700;
            color: #0c0c0c;
            line-height: 1;
            align-self: center;
          }

          .pc-ticket__tier-body {
            flex: 1;
            min-width: 0;
            border-bottom: 1px solid #fff;

            .pc-ticket__tier-meta {
              display: flex;
              flex-wrap: wrap;
              align-items: baseline;
              padding: 0 20px 10px 24px;

              .pc-ticket__tier-name {
                margin-right: 33px;
                font-size: 17px;
                font-weight: 700;

                span {
                  padding-right: 15px;
                }
              }

              .pc-ticket__tier-price {
                margin: 0;
                font-size: 14px;
                font-weight: 600;
                color: #fff;
                line-height: 1.4;
                white-space: nowrap;
              }
            }
          }
        }

        .pc-ticket__tier-dl {
          margin: 22px 0 0 84px;

          .pc-ticket__tier-pair {
            margin: 8px 0;
            display: flex;

            .pc-ticket__tier-term {
              margin: 0 20px 0 0;
              font-size: 13px;
              font-weight: 600;
              color: #fff;
              text-align: center;
              background: rgba(0, 0, 0, 0.25);
              padding: 0 14px;
              box-sizing: border-box;
              line-height: 24px;
              height: 24px;
            }

            .pc-ticket__tier-desc {
              margin: 0;
              padding: 8px 0;
              font-size: 16px;
              font-weight: 700;
              color: #fff;
              display: flex;
              align-items: center;
              line-height: 24px;
              height: 24px;
              font-family: 'Noto Sans JP', 'Noto Sans', sans-serif;
              font-style: normal;
            }
          }
        }
      }

      .pc-ticket__notes {
        padding: 24px 200px 0 242px;
        box-sizing: border-box;

        .pc-ticket__notes-h {
          margin: 0 0 8px;
          font-size: 14px;
          color: #fff;
          letter-spacing: 0.04em;
        }

        .pc-ticket__notes-ul {
          margin: 0;
          padding: 0;
          list-style: none;
          font-size: 14px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.92);

          li {
            display: block;
            margin-bottom: 6px;
            font-size: 14px;
            text-indent: -1.2em;
            padding-left: 1.2em;
            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }
    }
  }

  /* 叠在 __main(z-index:1) 之上，避免底部跑马灯半截被挡 */
  &__marquee-bottom-slot {
    position: relative;
    z-index: 4;
    margin-top: 0;
    height: 134px;
    pointer-events: none;
  }

  &__marquee {
    position: absolute;
    top: 306px;
    left: 0;
    width: 100%;
    height: 134px;
    background-image: url("@/assets/image/index/banner_footer_bg.webp");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    z-index: 10;

    &--bottom {
      top: 0;
      right: 0;
      bottom: auto;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;

      .pc-ticket__marquee-view {
        margin-top: 45px;
      }
    }

    .pc-ticket__marquee-view {
      margin-top: 45px;
      overflow: hidden;
      height: 44px;
      display: flex;
      align-items: center;
      background-color: #191616;
      border-top: 1px solid #fff590;
      border-bottom: 1px solid #fff590;

      .pc-ticket__marquee-track {
        display: flex;
        width: max-content;
        flex-shrink: 0;
        align-items: center;
        animation: pc-ticket-marquee 45s linear infinite;
        will-change: transform;

        @media (prefers-reduced-motion: reduce) {
          animation: none;
          will-change: auto;
        }

        .pc-ticket__marquee-chunk {
          display: flex;
          flex-shrink: 0;
          align-items: center;

          .pc-ticket__marquee-img {
            flex-shrink: 0;
            margin-right: 30px;
            width: 314px;
            height: 30px;
            object-fit: contain;
            object-position: center;
          }
        }
      }
    }
  }

  &__main {
    margin-top: -67px;
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 918px;
    background-color: #3c3068;
    overflow: hidden;
    border-bottom: 2px solid #a77dc3;

    .pc-ticket__main-bg-layer {
      position: absolute;
      inset: 0;
      z-index: 0;
      overflow: hidden;
      pointer-events: none;
      background-image: url("@/assets/image/index/dj_bg.webp");
      background-size: 1920px 918px;
      background-position: 0 0;
      background-repeat: repeat;
      animation: pc-ticket-main-bg-infinite 36s linear infinite;
      will-change: background-position;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
        will-change: auto;
      }
    }
  }

  &__main-content {
    margin: 108px auto 140px;
    color: #fff;

    &-time-line {
      text-align: center;

      /* 未入场：五张卡片贴中轴；入场后带错峰 delay 向左右展开 */
      &:not(.is-cards-revealed) {
        .pc-ticket__main-content-time-line-item--sapporo,
        .pc-ticket__main-content-time-line-item--nagoya,
        .pc-ticket__main-content-time-line-item--tokyo {
          opacity: 0;
          transform: translateX(140px) scale(0.9);
        }

        .pc-ticket__main-content-time-line-item--fukuoka,
        .pc-ticket__main-content-time-line-item--osaka {
          opacity: 0;
          transform: translateX(-140px) scale(0.9);
        }
      }

      &.is-cards-revealed {
        .pc-ticket__main-content-time-line-item--sapporo {
          opacity: 1;
          transform: translateX(0) scale(1);
          transition-delay: 0.05s;
        }

        .pc-ticket__main-content-time-line-item--fukuoka {
          opacity: 1;
          transform: translateX(0) scale(1);
          transition-delay: 0.13s;
        }

        .pc-ticket__main-content-time-line-item--nagoya {
          opacity: 1;
          transform: translateX(0) scale(1);
          transition-delay: 0.21s;
        }

        .pc-ticket__main-content-time-line-item--osaka {
          opacity: 1;
          transform: translateX(0) scale(1);
          transition-delay: 0.29s;
        }

        .pc-ticket__main-content-time-line-item--tokyo {
          opacity: 1;
          transform: translateX(0) scale(1);
          transition-delay: 0.37s;
        }
      }

      &-item-year {
        font-size: 24px;
        font-weight: 700;
      }

      &-item {
        position: relative;
        &-time {
          position: relative;
          margin-top: 120px;
          font-size: 40px;
          font-weight: 700;

          span {
            padding-left: 6px;
            font-size: 16px;
            left: 50%;
            transform: translate(-50%, -50%);
            color: #36e9ff;
          }

          &-dow {
            color: #ff835d !important;
          }
        }

        &-time::before {
          content: "";
          position: absolute;
          top: -64px;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 2px;
          height: 88px;
          background-color: #fff380;
        }

        &-content {
          padding-top: 70px;
          display: flex;
          flex-direction: column;
          position: absolute;
          bottom: -10px;
          left: -460px;
          width: 384px;
          height: 209px;
          transform-origin: center center;
          transition: opacity 0.75s cubic-bezier(0.22, 1, 0.32, 1),
            transform 0.75s cubic-bezier(0.22, 1, 0.32, 1);
          background-size: 100% 100%;
          background-position: center;
          background-repeat: no-repeat;

          &-line {
            margin: 13px 100px 10px 80px;
            display: flex;
            align-items: center;
            font-size: 14px;

            .label {
              width: 64px;
              margin-right: 10px;
              font-size: 10px;
              font-weight: 700;
              padding: 0px 4px;
              border-radius: 4px;
              color: #191616;
            }

            .value {
              text-align: left;
              min-width: 140px;
            }
          }
        }

        &--sapporo {
          background-image: url("@/assets/image/ticket/card_bg_1.webp");
          .label {
            background-color: #36e9ff;
          }

          .pc-ticket__main-content-time-line-item-content-line:last-child {
            border-top: 2px solid #36e9ff;
            padding-top: 12px;
            margin-top: 6px;
          }
        }

        &--fukuoka {
          left: auto;
          right: -460px;
          background-image: url("@/assets/image/ticket/card_bg_2.webp");

          .pc-ticket__main-content-time-line-item-content-line {
            margin: 13px 80px 10px 100px;
          }

          .label {
            background-color: #d9a2ff;
          }

          .pc-ticket__main-content-time-line-item-content-line:last-child {
            border-top: 2px solid #d9a2ff;
            padding-top: 12px;
            margin-top: 6px;
          }
        }

        &--nagoya {
          background-image: url("@/assets/image/ticket/card_bg_3.webp");
          .label {
            background-color: #54ffbd;
          }

          .pc-ticket__main-content-time-line-item-content-line:last-child {
            border-top: 2px solid #54ffbd;
            padding-top: 12px;
            margin-top: 6px;
          }
        }

        &--osaka {
          left: auto;
          right: -460px;
          background-image: url("@/assets/image/ticket/card_bg_4.webp");
          .pc-ticket__main-content-time-line-item-content-line {
            margin: 13px 80px 10px 100px;
          }
          .label {
            background-color: #ffab91;
          }

          .pc-ticket__main-content-time-line-item-content-line:last-child {
            border-top: 2px solid #ffab91;
            padding-top: 12px;
            margin-top: 6px;
          }
        }

        &--tokyo {
          background-image: url("@/assets/image/ticket/card_bg_5.webp");
          .label {
            background-color: #fff6a2;
            color: #191616;
          }

          .pc-ticket__main-content-time-line-item-content-line:last-child {
            border-top: 2px solid #fff6a2;
            padding-top: 12px;
            margin-top: 6px;
          }
        }

        @media (max-width: 1600px) {
          &-content {
            left: -400px;
          }

          &--fukuoka,
          &--osaka {
            left: auto;
            right: -400px;
          }
        }

        @media (max-width: 1440px) {
          &-content {
            left: -390px;
          }

          &--fukuoka,
          &--osaka {
            left: auto;
            right: -390px;
          }
        }
      }
    }
  }
}

/* YoStation 下载 QR 全屏弹窗（与首页一致） */
.pc-ticket-qr-modal {
  position: fixed;
  inset: 0;
  z-index: 301;
  background-color: #35353c;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 72px 24px 32px;
  box-sizing: border-box;
  border-top: 2px solid #c84cff;
  border-bottom: 2px solid #c84cff;
  box-shadow: 0 -8px 22px -2px rgba(191, 0, 255, 0.55),
    0 8px 22px -2px rgba(191, 0, 255, 0.55),
    0 -18px 40px -6px rgba(200, 76, 255, 0.35),
    0 18px 40px -6px rgba(200, 76, 255, 0.35);

  &__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  &__close {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 40px;
    height: 40px;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    border-radius: 50%;
    transform-origin: center center;
    -webkit-tap-highlight-color: transparent;
    transition: transform 0.24s cubic-bezier(0.34, 1.45, 0.64, 1),
      filter 0.2s ease, background-color 0.2s ease, box-shadow 0.24s ease;

    img {
      display: block;
      width: 40px;
      height: 40px;
      object-fit: contain;
      pointer-events: none;
      transition: transform 0.24s cubic-bezier(0.34, 1.45, 0.64, 1),
        filter 0.2s ease;
    }

    &:hover {
      transform: scale(1.12);
      filter: brightness(1.12) drop-shadow(0 4px 12px rgba(255, 243, 128, 0.35));

      img {
        transform: rotate(90deg);
      }
    }

    &:active {
      transform: scale(0.9);
      filter: brightness(0.9);
      transition-duration: 0.08s;

      img {
        transform: rotate(90deg) scale(0.95);
        transition-duration: 0.08s;
      }
    }

    &:focus-visible {
      outline: 2px solid #fff380;
      outline-offset: 2px;
    }
  }

  &__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    max-width: 100%;
  }

  &__qr-frame {
    background-color: #fff;
    padding: 20px;
    box-sizing: border-box;
    line-height: 0;
    max-width: 100%;
    border-radius: 8px;
    overflow: hidden;
  }

  &__qr {
    display: block;
    width: 360px;
    height: 360px;
    max-width: 100%;
    max-height: min(360px, 55vh);
    object-fit: contain;
  }

  &__caption {
    margin: 24px 0 0;
    padding: 0;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.5;
    text-align: center;
  }
}

.pc-ticket-qr-modal.pc-ticket-qr-modal-enter-active,
.pc-ticket-qr-modal.pc-ticket-qr-modal-leave-active {
  transition: opacity 0.32s cubic-bezier(0.4, 0, 0.2, 1);
}

.pc-ticket-qr-modal.pc-ticket-qr-modal-enter-active .pc-ticket-qr-modal__inner,
.pc-ticket-qr-modal.pc-ticket-qr-modal-leave-active .pc-ticket-qr-modal__inner {
  transition: transform 0.4s cubic-bezier(0.34, 1.18, 0.64, 1),
    opacity 0.32s ease;
}

.pc-ticket-qr-modal.pc-ticket-qr-modal-enter,
.pc-ticket-qr-modal.pc-ticket-qr-modal-leave-to {
  opacity: 0;
}

.pc-ticket-qr-modal.pc-ticket-qr-modal-enter .pc-ticket-qr-modal__inner,
.pc-ticket-qr-modal.pc-ticket-qr-modal-leave-to .pc-ticket-qr-modal__inner {
  opacity: 0;
  transform: scale(0.92) translateY(20px);
}

@media (prefers-reduced-motion: reduce) {
  .pc-ticket-qr-modal.pc-ticket-qr-modal-enter-active,
  .pc-ticket-qr-modal.pc-ticket-qr-modal-leave-active {
    transition: none !important;
  }

  .pc-ticket-qr-modal.pc-ticket-qr-modal-enter-active
    .pc-ticket-qr-modal__inner,
  .pc-ticket-qr-modal.pc-ticket-qr-modal-leave-active
    .pc-ticket-qr-modal__inner {
    transition: none !important;
  }

  .pc-ticket-qr-modal.pc-ticket-qr-modal-enter .pc-ticket-qr-modal__inner,
  .pc-ticket-qr-modal.pc-ticket-qr-modal-leave-to .pc-ticket-qr-modal__inner {
    transform: none !important;
    opacity: 1 !important;
  }

  .pc-ticket-qr-modal__close,
  .pc-ticket-qr-modal__close img {
    transition: none !important;
  }

  .pc-ticket-qr-modal__close:hover,
  .pc-ticket-qr-modal__close:active {
    transform: none !important;
    filter: none !important;
  }

  .pc-ticket-qr-modal__close:hover img,
  .pc-ticket-qr-modal__close:active img {
    transform: none !important;
  }
}

@keyframes pc-ticket-deco-beat {
  0%,
  42% {
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.45, 0, 0.55, 1);
  }
  52% {
    transform: scale(0.9);
    animation-timing-function: cubic-bezier(0.55, 0.05, 0.6, 0.45);
  }
  58% {
    transform: scale(0.86);
    animation-timing-function: cubic-bezier(0.33, 1.15, 0.52, 1);
  }
  70% {
    transform: scale(1.08);
    animation-timing-function: cubic-bezier(0.22, 0.85, 0.38, 1);
  }
  80% {
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes pc-ticket-marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

/* 单背景按素材原始宽度平铺并整宽位移，形成无缝无限滚动 */
@keyframes pc-ticket-main-bg-infinite {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: -1920px 0;
  }
}
</style>
